// Frontend entry point for the permission catalogue. The catalogue itself lives
// in the cross-stack `@synapse/permissions` package (shared with the backend
// guards), so the keys this app renders in the role editor and the keys the API
// enforces are the same declarations — nothing is mirrored, so the two sides
// cannot drift.
//
// What belongs here rather than in the package is the part that is genuinely
// the client's: turning a key into words a person can read, which needs a
// translator the package must not depend on.
//
// Mirrors how `src/lib/itemTypeConfig.ts` re-exports `@synapse/item-types`.

export * from '@synapse/permissions';

import type { PermissionKey, PermissionGroupId } from '@synapse/permissions';
import type { TFunction } from 'i18next';

/**
 * Label and one-line description for a permission, from the `permissions.*`
 * namespace. Keys are dotted, and i18next reads dots as nesting, so each key is
 * flattened to a single segment before lookup — `patients.photo.manage` becomes
 * `permissions.keys.patients_photo_manage_label`.
 */
export function permissionLabel(t: TFunction, key: PermissionKey): string {
  return t(`permissions.keys.${flatten(key)}_label`);
}

export function permissionDescription(t: TFunction, key: PermissionKey): string {
  return t(`permissions.keys.${flatten(key)}_description`);
}

export function permissionGroupLabel(t: TFunction, id: PermissionGroupId): string {
  return t(`permissions.groups.${id}`);
}

function flatten(key: PermissionKey): string {
  return key.replace(/\./g, '_');
}
